import { getWeeksInMonth, today } from '@internationalized/date';
import type { CalendarState } from '@react-stately/calendar';
import { useMemo, useRef } from 'react';
import { useCalendarGrid } from 'react-aria';

import { useDatePickerConfig } from '../../contexts/ConfigContext';
import { CalendarCell } from './CalendarCell';

interface DayViewProps {
  state: CalendarState;
  gridRef: React.RefObject<HTMLDivElement | null>;
}

export function DayView({ state, gridRef }: DayViewProps) {
  const internalGridRef = useRef<HTMLDivElement>(null);
  const { locale } = useDatePickerConfig();
  const effectiveGridRef = gridRef || internalGridRef;
  const { gridProps, headerProps, weekDays } = useCalendarGrid({ weekdayStyle: 'short' }, state);

  // Truncate weekday names to 2 letters
  const twoLetterWeekDays = weekDays.map((day) => day.slice(0, 2));

  const weeksInMonth = useMemo(
    () => getWeeksInMonth(state.visibleRange.start, locale),
    [state.visibleRange.start, locale],
  );

  return (
    <div className="p-3">
      {/* Week day headers */}
      <div {...headerProps} className="d-grid mb-2" style={{ gridTemplateColumns: 'repeat(7, 1fr)' }}>
        {twoLetterWeekDays.map((day) => (
          <div
            key={day}
            style={{ width: `${CELL_SIZE}px`, height: `${CELL_SIZE}px` }}
            className="text-secondary fw-medium text-capitalize justify-content-center d-flex align-items-center"
          >
            {day}
          </div>
        ))}
      </div>

      <div
        {...gridProps}
        ref={effectiveGridRef}
        className="d-grid"
        style={{ gridTemplateColumns: 'repeat(7, 1fr)' }}
        data-selected-date={state.value?.toString()}
        data-today-date={today(state.timeZone).toString()}
      >
        {[...new Array(weeksInMonth).keys()].flatMap((weekIndex) =>
          state
            .getDatesInWeek(weekIndex)
            .map((date) =>
              date ? (
                <CalendarCell key={date.toString()} state={state} date={date} />
              ) : (
                <div key={`empty-${weekIndex}`} style={{ width: `${CELL_SIZE}px`, height: `${CELL_SIZE}px` }} />
              ),
            ),
        )}
      </div>
    </div>
  );
}

const CELL_SIZE = 40;
